import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  forwardRef,
  type MouseEventHandler,
  type Ref,
} from 'react';
import styled, { css } from 'styled-components';
import type { XOR } from 'ts-essentials';

import { mixins } from '#@/shared/utils/styles';

interface OnClick {
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
}

export type ClickableAnchorProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href' | 'onClick'
> &
  OnClick &
  XOR<{ linkTo: string }, { linkToExternal: string }>;

export type ClickableButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> &
  OnClick;

export type ClickableProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick'> &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
    linkTo?: string;
    linkToExternal?: string;
    onClick?: MouseEventHandler<HTMLAnchorElement & HTMLButtonElement>;
  };

const isLink = (props: ClickableProps): boolean => Boolean(props.linkTo ?? props?.linkToExternal);

const ClickableBase = forwardRef<HTMLButtonElement, ClickableProps>((props, ref) => {
  const { linkTo, linkToExternal, target, rel, ...rest } = props;
  return isLink(props) ? (
    <a
      ref={ref as Ref<HTMLAnchorElement>}
      {...rest}
      href={linkTo ?? linkToExternal}
      target={linkToExternal ? '_blank' : target}
      rel={linkToExternal ? 'noopener noreferrer' : rel}
    />
  ) : (
    <button type="button" ref={ref} {...rest} />
  );
});

ClickableBase.displayName = 'ClickableBase';

const Clickable = styled(ClickableBase)`
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  text-align: left;
  ${mixins.clickable()}
  ${(props) =>
    props.disabled &&
    css`
      cursor: default;
    `}
`;

export default Clickable;
